import { AnimatePresence, motion } from "framer-motion";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import type { InstanceStatus } from "./blackboardModels";

import {
  useRestartInstance,
  useWakeIfPending,
} from "./useInstanceLifecycleActions";

interface InstanceActionMenuProps {
  instanceId: string;
  status: InstanceStatus;
  killing: boolean;
  onKill: () => void;
  onLifecycleChange?: () => void;
}

type ConfirmKind = "kill" | "restart" | null;
type ResultTone = "danger" | "muted" | "success";

const RESTART_WARNING =
  "Restart tears down and respawns the actor — context and mailbox reset.";
const KILL_WARNING =
  "Kill permanently terminates this instance. This cannot be undone.";

const RESULT_TONE: Record<ResultTone, string> = {
  danger: "border-rose-500/30 text-rose-600 dark:text-rose-300",
  muted: "border-slate-200 text-slate-500 dark:border-slate-800/80",
  success: "border-emerald-500/30 text-emerald-700 dark:text-emerald-300",
};

const ITEM_TONE = {
  danger:
    "text-rose-700 hover:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/10",
  success:
    "text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/10",
} as const;

/**
 * Single overflow (⋮) menu consolidating an actor's lifecycle actions —
 * wake-if-pending, restart, kill — into the drawer header. Replaces the
 * always-on Kill button + the separate LifecycleControls strip so destructive
 * verbs live in one predictable place. Restart/kill route through an inline
 * confirm; the status-aware restart warning preserves the "interrupts the
 * current wake" caution that the old RUNNING risk chip carried.
 */
export const InstanceActionMenu: FC<InstanceActionMenuProps> = ({
  instanceId,
  status,
  killing,
  onKill,
  onLifecycleChange,
}) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [alsoStopRunningSpawns, setAlsoStopRunningSpawns] = useState(false);
  const [result, setResult] = useState<{
    text: string;
    tone: ResultTone;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    error: restartError,
    pending: restarting,
    restartInstance,
  } = useRestartInstance();
  const {
    error: wakeError,
    pending: waking,
    wakeIfPending,
  } = useWakeIfPending();
  const disabled = status === "killed";
  const actionError = restartError ?? wakeError;

  const close = useCallback(() => {
    setOpen(false);
    setConfirm(null);
    setAlsoStopRunningSpawns(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClick(event: MouseEvent) {
      if (
        event.target instanceof Node &&
        containerRef.current?.contains(event.target)
      ) {
        return;
      }
      close();
    }
    function handleKey(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        close();
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, close]);

  const fireWake = useCallback(async () => {
    setResult(null);
    try {
      const outcome = await wakeIfPending(instanceId);
      setResult(
        outcome.dispatched
          ? { text: "wake dispatched", tone: "success" }
          : { text: "no pending wake", tone: "muted" },
      );
      if (outcome.dispatched) {
        onLifecycleChange?.();
      }
    } catch {
      // Surfaced via wakeError
    }
  }, [instanceId, onLifecycleChange, wakeIfPending]);

  const fireRestart = useCallback(async () => {
    setResult(null);
    try {
      const outcome = await restartInstance(instanceId, {
        alsoStopRunningSpawns,
      });
      setConfirm(null);
      setAlsoStopRunningSpawns(false);
      setResult(
        outcome.status === "already_killed"
          ? { text: "already killed", tone: "muted" }
          : { text: "restart queued", tone: "success" },
      );
      onLifecycleChange?.();
    } catch {
      // Surfaced via restartError
    }
  }, [alsoStopRunningSpawns, instanceId, onLifecycleChange, restartInstance]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Instance actions"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-6 w-6 items-center justify-center border border-slate-300 font-mono text-[14px] leading-none text-slate-600 transition-colors hover:border-emerald-400/60 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-400 dark:hover:text-emerald-300"
      >
        ⋮
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.98, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ bounce: 0, duration: 0.16, type: "spring" }}
            className="absolute right-0 top-full z-50 mt-1 w-60 border border-slate-300 bg-white shadow-[4px_4px_0_rgba(15,23,42,0.18)] dark:border-emerald-400/30 dark:bg-slate-950/95 dark:shadow-[4px_4px_0_rgba(2,6,23,0.6)]"
          >
            <p className="border-b border-slate-200 px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:border-slate-800/80">
              [ Actions ]
            </p>

            {confirm === null && (
              <div className="flex flex-col">
                {status !== "killed" && (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={waking}
                      onClick={() => void fireWake()}
                      className={`px-3 py-2 text-left font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-colors disabled:opacity-50 ${ITEM_TONE.success}`}
                    >
                      {waking ? "Checking…" : "Wake if pending"}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setResult(null);
                        setAlsoStopRunningSpawns(false);
                        setConfirm("restart");
                      }}
                      className={`px-3 py-2 text-left font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${ITEM_TONE.danger}`}
                    >
                      Restart…
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setResult(null);
                        setConfirm("kill");
                      }}
                      className={`px-3 py-2 text-left font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${ITEM_TONE.danger}`}
                    >
                      Kill…
                    </button>
                  </>
                )}
              </div>
            )}

            {confirm === "restart" && (
              <ConfirmPanel
                warning={
                  status === "running"
                    ? `${RESTART_WARNING} The actor is RUNNING; this interrupts the current wake.`
                    : RESTART_WARNING
                }
                confirmLabel={restarting ? "Restarting…" : "Confirm restart"}
                pending={restarting}
                onCancel={() => {
                  setConfirm(null);
                  setAlsoStopRunningSpawns(false);
                }}
                onConfirm={() => void fireRestart()}
              >
                <label className="flex items-start gap-2 border border-rose-500/20 bg-white/50 p-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-rose-700 dark:bg-slate-950/40 dark:text-rose-200">
                  <input
                    type="checkbox"
                    checked={alsoStopRunningSpawns}
                    disabled={restarting}
                    onChange={(event) =>
                      setAlsoStopRunningSpawns(event.target.checked)
                    }
                    className="mt-0.5 h-3 w-3 accent-rose-600"
                  />
                  <span>Also stop running process spawns</span>
                </label>
              </ConfirmPanel>
            )}

            {confirm === "kill" && (
              <ConfirmPanel
                warning={KILL_WARNING}
                confirmLabel={killing ? "Killing…" : "Confirm kill"}
                pending={killing}
                onCancel={() => setConfirm(null)}
                onConfirm={onKill}
              />
            )}

            {(actionError ?? result) && (
              <p
                className={`border-t px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest ${
                  actionError
                    ? RESULT_TONE.danger
                    : RESULT_TONE[result?.tone ?? "muted"]
                }`}
              >
                {actionError ?? result?.text}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ConfirmPanel: FC<{
  children?: ReactNode;
  warning: string;
  confirmLabel: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ children, warning, confirmLabel, pending, onCancel, onConfirm }) => (
  <div className="flex flex-col gap-2 border-t border-rose-500/30 bg-rose-500/[0.04] p-2">
    <p className="font-mono text-[10px] leading-relaxed text-rose-700 dark:text-rose-200">
      {warning}
    </p>
    {children}
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        disabled={pending}
        onClick={onCancel}
        className="border border-slate-300 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-slate-600 transition-colors hover:border-emerald-400/60 hover:text-emerald-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:text-emerald-300"
      >
        Cancel
      </button>
      <button
        type="button"
        disabled={pending}
        onClick={onConfirm}
        className="border border-rose-500/50 bg-rose-500/5 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-rose-700 transition-colors hover:border-rose-500/70 hover:bg-rose-500/10 disabled:opacity-50 dark:border-rose-500/40 dark:text-rose-300"
      >
        {confirmLabel}
      </button>
    </div>
  </div>
);
